'use client';

import { Box, HStack, VStack, css } from '@terra/ui-styled-system';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  Copy01Icon,
  File01Icon,
  Globe02Icon,
  Mail01Icon,
  ServerStack01Icon,
} from '@terra/ui-icons';
import { Text } from '../../../components/styled/text';
import { Badge } from '../../../components/badge';
import { IconButton } from '../../../components/icon-button';
import { Tooltip } from '../../../components/tooltip';
import { Collapsible } from '../../../components/collapsible';
import type { InteractMentionData } from '@terra/shared-dto-chat-types';

type InteractMentionCardProps = {
  data: InteractMentionData;
  compact?: boolean;
};
const cardStyles = css({
  borderRadius: 'md',
  borderWidth: '1px',
  borderColor: 'border.muted',
  bg: 'bg.subtle',
  overflow: 'hidden',
  fontSize: 'xs',
});
const headerStyles = css({
  px: 3,
  py: 2,
  bg: 'bg.muted',
  borderBottomWidth: '1px',
  borderColor: 'border.muted',
  cursor: 'pointer',
  _hover: {
    bg: 'bg.hover',
  },
});
const codeBlockStyles = css({
  fontFamily: 'mono',
  fontSize: '2xs',
  bg: 'bg.canvas',
  p: 2,
  borderRadius: 'sm',
  overflow: 'auto',
  maxHeight: '200px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
});

function getProtocolIcon(protocol: string) {
  switch (protocol.toLowerCase()) {
    case 'http':
      return Globe02Icon;
    case 'dns':
      return ServerStack01Icon;
    case 'smtp':
      return Mail01Icon;
    case 'ftp':
      return File01Icon;
    default:
      return Globe02Icon;
  }
}

function formatTimestamp(timestamp: string): string {
  try {
    return new Date(timestamp).toLocaleString();
  } catch {
    return timestamp;
  }
}

export function InteractMentionCard({
  data,
  compact = false,
}: InteractMentionCardProps) {
  const t = useTranslations('designSystem.chat.interactMentionCard');
  const tInteractsh = useTranslations('designSystem.chat.interactsh');
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const ProtocolIcon = getProtocolIcon(data.protocol);
  const hasRawRequest = !!data.rawRequest;
  const hasRawResponse = !!data.rawResponse;
  const hasHttpDetails = !!data.httpMethod || !!data.httpPath;
  const handleCopy = async (text: string, section: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 1500);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleCopyAll = () => {
    handleCopy(data.rawText, 'all');
  };

  return (
    <Box className={cardStyles}>
      <HStack
        className={headerStyles}
        justifyContent="space-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <HStack gap={2} flex={1} minW={0}>
          <Box color="fg.muted" flexShrink={0}>
            {isExpanded ? (
              <ArrowDown01Icon size={14} />
            ) : (
              <ArrowRight01Icon size={14} />
            )}
          </Box>

          <Badge
            size="sm"
            variant="fill"
            leadingContent={<ProtocolIcon size={10} />}
          >
            {data.protocol.toUpperCase()}
          </Badge>

          {hasHttpDetails && (
            <Badge size="sm" variant="emphasis">
              {data.httpMethod} {data.httpPath}
            </Badge>
          )}

          {data.qType && (
            <Badge size="sm" variant="emphasis">
              {data.qType}
            </Badge>
          )}

          <Text
            fontSize="xs"
            color="fg.muted"
            truncate
            flex={1}
            fontFamily="mono"
          >
            {data.remoteAddress}
          </Text>
        </HStack>

        <HStack gap={1} flexShrink={0} onClick={(e) => e.stopPropagation()}>
          <Tooltip title={copiedSection === 'all' ? 'Copied!' : 'Copy all'}>
            <IconButton
              size="xs"
              variant="ghost"
              aria-label="Copy all"
              onClick={handleCopyAll}
            >
              <Copy01Icon size={12} />
            </IconButton>
          </Tooltip>
        </HStack>
      </HStack>

      <Collapsible.Root open={isExpanded}>
        <Collapsible.Content>
          <VStack gap={2} p={3} alignItems="stretch">
            <HStack gap={4} flexWrap="wrap">
              <Box>
                <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                  {t('domain')}
                </Text>
                <Text fontSize="xs" fontFamily="mono" color="fg.default">
                  {data.domain}
                </Text>
              </Box>
              <Box>
                <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                  {t('timestamp')}
                </Text>
                <Text fontSize="xs" color="fg.default">
                  {formatTimestamp(data.timestamp)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                  {t('remoteAddress')}
                </Text>
                <Text fontSize="xs" fontFamily="mono" color="fg.default">
                  {data.remoteAddress}
                </Text>
              </Box>
            </HStack>

            {data.httpHeaders && Object.keys(data.httpHeaders).length > 0 && (
              <Box>
                <HStack justifyContent="space-between" mb={1}>
                  <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                    {t('httpHeaders', {
                      count: Object.keys(data.httpHeaders).length,
                    })}
                  </Text>
                  <Tooltip title="Copy headers">
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label="Copy headers"
                      onClick={() =>
                        handleCopy(
                          Object.entries(data.httpHeaders || {})
                            .map(([k, v]) => `${k}: ${v}`)
                            .join('\n'),
                          'headers',
                        )
                      }
                    >
                      <Copy01Icon size={10} />
                    </IconButton>
                  </Tooltip>
                </HStack>
                <Box className={codeBlockStyles}>
                  {Object.entries(data.httpHeaders)
                    .slice(0, compact ? 3 : undefined)
                    .map(([key, value]) => (
                      <Box key={key}>
                        <Text as="span" color="fg.muted">
                          {key}:
                        </Text>{' '}
                        <Text as="span" color="fg.default">
                          {value}
                        </Text>
                      </Box>
                    ))}
                </Box>
              </Box>
            )}

            {hasRawRequest && (
              <Box>
                <HStack justifyContent="space-between" mb={1}>
                  <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                    {tInteractsh('request')}
                  </Text>
                  <Tooltip title="Copy request">
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label="Copy request"
                      onClick={() =>
                        handleCopy(data.rawRequest || '', 'request')
                      }
                    >
                      <Copy01Icon size={10} />
                    </IconButton>
                  </Tooltip>
                </HStack>
                <Box className={codeBlockStyles}>
                  <Text fontSize="2xs" color="fg.default" whiteSpace="pre-wrap">
                    {compact && data.rawRequest && data.rawRequest.length > 300
                      ? data.rawRequest.slice(0, 300) + '...'
                      : data.rawRequest}
                  </Text>
                </Box>
              </Box>
            )}

            {hasRawResponse && (
              <Box>
                <HStack justifyContent="space-between" mb={1}>
                  <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                    {tInteractsh('response')}
                  </Text>
                  <Tooltip title="Copy response">
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label="Copy response"
                      onClick={() =>
                        handleCopy(data.rawResponse || '', 'response')
                      }
                    >
                      <Copy01Icon size={10} />
                    </IconButton>
                  </Tooltip>
                </HStack>
                <Box className={codeBlockStyles}>
                  <Text fontSize="2xs" color="fg.default" whiteSpace="pre-wrap">
                    {compact &&
                    data.rawResponse &&
                    data.rawResponse.length > 300
                      ? data.rawResponse.slice(0, 300) + '...'
                      : data.rawResponse}
                  </Text>
                </Box>
              </Box>
            )}
          </VStack>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}
